import { useRef, useCallback } from "react";
import * as THREE from "three";

/**
 * Section-cut hook.
 * Owns a single horizontal `THREE.Plane(0,-1,0)` whose constant is set to
 * the selected floor's ceiling Y. The plane is applied PER-MATERIAL onto every
 * mesh inside the houseGroup (NOT the renderer's global planes) so the env
 * group (ground / walkways / trees) remains uncut.
 *
 * Usage:
 *   const { clipPlaneRef, applyClipping } = useSectionCut();
 *   // After (re)building the house each blueprint pass:
 *   applyClipping(houseGroup, selectedCutY);
 *   // selectedCutY: number | null. null → clear all clipping.
 *
 * @returns {object} { clipPlaneRef, applyClipping }
 */
export default function useSectionCut() {
  const clipPlaneRef = useRef(null);
  if (clipPlaneRef.current == null) {
    clipPlaneRef.current = new THREE.Plane(new THREE.Vector3(0, -1, 0), 1000);
  }

  const applyClipping = useCallback((houseGroup, selectedCutY) => {
    if (!houseGroup) return;
    const plane = clipPlaneRef.current;
    const enable = plane && selectedCutY != null && Number.isFinite(selectedCutY);
    if (enable) {
      plane.constant = selectedCutY;
    }
    houseGroup.traverse((node) => {
      if (!node.isMesh && !node.isLineSegments) return;
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach((m) => {
        if (!m) return;
        if (enable) {
          m.clippingPlanes = [plane];
          m.clipShadows = true;
        } else {
          m.clippingPlanes = [];
          m.clipShadows = false;
        }
      });
    });
  }, []);

  return { clipPlaneRef, applyClipping };
}
